import React, { useState, useEffect } from 'react';
import { Operator, PackageItem, Wallet, OperatorId, getAvailableBalance, getReservedBalance } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { OperatorSelector } from '../components/shop/OperatorSelector';
import { PackageCard } from '../components/shop/PackageCard';
import { CheckoutModal } from '../components/shop/CheckoutModal';
import { DepositModal } from '../components/wallet/DepositModal';
import { SellBillModal } from '../components/buyback/SellBillModal';
import { BuyBillModal } from '../components/buyback/BuyBillModal';
import { Wallet as WalletIcon, PlusCircle, ArrowLeftRight, PhoneCall, ShieldAlert } from 'lucide-react';

interface ShopPageProps {
  onNavigateToOrders: () => void;
}

export const ShopPage: React.FC<ShopPageProps> = ({ onNavigateToOrders }) => {
  const { t } = useLanguage();
  const { user, isGuest } = useAuth();
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperatorId, setSelectedOperatorId] = useState<OperatorId>('mpt');
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [selectedPackage, setSelectedPackage] = useState<PackageItem | null>(null);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);

  const handleOpenDeposit = () => {
    if (user.is_banned === 1) {
      alert(`သင်၏အကောင့်အား ယာယီပိတ်ပင်ထားပါသဖြင့် ငွေသွင်း၍မရပါ။\nအကြောင်းရင်း: ${user.ban_reason || 'စည်းကမ်းဖောက်ဖျက်မှုကြောင့်'}`);
      return;
    }
    setShowDepositModal(true);
  };

  const handleOpenBuyBill = () => {
    if (user.is_banned === 1) {
      alert(`သင်၏အကောင့်အား ယာယီပိတ်ပင်ထားပါသဖြင့် ဘေလ်ဝယ်၍မရပါ။\nအကြောင်းရင်း: ${user.ban_reason || 'စည်းကမ်းဖောက်ဖျက်မှုကြောင့်'}`);
      return;
    }
    setShowBuyModal(true);
  };

  const handleOpenSellBill = () => {
    if (user.is_banned === 1) {
      alert(`သင်၏အကောင့်အား ယာယီပိတ်ပင်ထားပါသဖြင့် ဘေလ်ရောင်း၍မရပါ။\nအကြောင်းရင်း: ${user.ban_reason || 'စည်းကမ်းဖောက်ဖျက်မှုကြောင့်'}`);
      return;
    }
    setShowSellModal(true);
  };

  const handleSelectPackage = (pkg: PackageItem) => {
    if (user.is_banned === 1) {
      alert(`သင်၏အကောင့်အား ယာယီပိတ်ပင်ထားပါသဖြင့် Package ဝယ်ယူ၍မရပါ။\nအကြောင်းရင်း: ${user.ban_reason || 'စည်းကမ်းဖောက်ဖျက်မှုကြောင့်'}`);
      return;
    }
    setSelectedPackage(pkg);
  };

  const loadData = async () => {
    try {
      // 1. Always fetch operators first so store catalog is never blocked
      const ops = await api.getOperators().catch((err) => {
        console.error('Failed to load operators:', err);
        return [];
      });
      setOperators(ops);

      // 2. Fetch customer wallet balance if user is identified
      if (user.id) {
        try {
          const wData = await api.getWallet();
          if (wData?.wallet) setWallet(wData.wallet);
        } catch (e) {
          console.error('Failed to load wallet:', e);
        }
      }
    } catch (err) {
      console.error('ShopPage loadData error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user.id, isGuest]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'buy_airtime' || params.get('action') === 'buy_bill') {
      setShowBuyModal(true);
    }
  }, []);

  useEffect(() => {
    api.getPackages(selectedOperatorId).then(setPackages).catch(() => {});
  }, [selectedOperatorId]);

  const currentOperator = operators.find((op) => op.id === selectedOperatorId);

  return (
    <div className="space-y-4 pb-24">
      {/* Banned Alert Banner */}
      {user.is_banned === 1 && (
        <div className="bg-red-950/80 border border-red-500/70 rounded-2xl p-4 text-red-200 shadow-md space-y-2 animate-fadeIn">
          <div className="flex items-center space-x-2">
            <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
            <h3 className="font-bold text-sm text-red-100">
              သင်၏အကောင့်အား ယာယီပိတ်ပင်ထားပါသည် (Account Banned)
            </h3>
          </div>
          <p className="text-xs text-red-300/90 leading-relaxed">
            {user.ban_reason
              ? `အကြောင်းရင်း: ${user.ban_reason}`
              : 'စနစ်စည်းကမ်း ဖောက်ဖျက်မှု သို့မဟုတ် မူမမှန်သော လုပ်ဆောင်ချက်များကြောင့် အကောင့်အား ကန့်သတ်ထားပါသည်။'}
          </p>
          <p className="text-[11px] text-red-400/90">
            လက်ရှိတွင် ဝယ်ယူမှု၊ ငွေသွင်းမှုနှင့် ဘေလ်ရောင်းချမှုများကို ပြုလုပ်၍ မရသေးပါ။
          </p>
        </div>
      )}

      {/* Wallet Summary / Welcome Banner (Compact & Clear) */}
      {isGuest ? (
        <div className="bg-gradient-to-br from-[#0F2942] via-[#0B1D30] to-[#08121E] rounded-2xl p-3.5 sm:p-4 text-white shadow-md border border-sky-500/25">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 rounded-lg bg-white/10 border border-white/10">
                <WalletIcon className="w-4 h-4 text-primary-light" />
              </div>
              <span className="text-xs font-bold text-white">PluvP Data & Airtime</span>
            </div>
            <a
              href="https://t.me/pluvp_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-white/20 border border-white/15 text-white font-medium px-2.5 py-0.5 rounded-full text-[10px] transition"
            >
              Telegram Bot
            </a>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-3 pt-2.5 border-t border-white/10">
            <button
              onClick={handleOpenDeposit}
              className="flex items-center justify-center space-x-1 bg-primary hover:bg-primary-hover active:scale-[0.98] font-bold py-2 px-2 rounded-xl text-xs transition shadow-sm text-white touch-target"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>{t.deposit}</span>
            </button>
            <button
              onClick={handleOpenBuyBill}
              className="flex items-center justify-center space-x-1 bg-sky-500/20 hover:bg-sky-500/30 active:scale-[0.98] text-white border border-sky-400/30 font-semibold py-2 px-2 rounded-xl text-xs transition backdrop-blur-xs touch-target"
            >
              <PhoneCall className="w-3.5 h-3.5 text-sky-400" />
              <span>{t.buyBill}</span>
            </button>
            <button
              onClick={handleOpenSellBill}
              className="flex items-center justify-center space-x-1 bg-white/10 hover:bg-white/15 active:scale-[0.98] text-white border border-white/20 font-semibold py-2 px-2 rounded-xl text-xs transition backdrop-blur-xs touch-target"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
              <span>{t.sellBill}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-[#0F2942] via-[#0B1D30] to-[#08121E] rounded-2xl p-3.5 sm:p-4 text-white shadow-md border border-sky-500/25">
          {/* Top Row: Available Balance + Reserved */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-white/60 block leading-tight">
                {t.available}
              </span>
              <div className="flex items-baseline space-x-1.5 mt-1">
                <span className="text-2xl sm:text-3xl font-bold font-mono tracking-tight text-white leading-none">
                  {getAvailableBalance(wallet).toLocaleString()}
                </span>
                <span className="text-xs font-semibold text-sky-400 font-mono">Ks</span>
              </div>
            </div>

            {wallet && getReservedBalance(wallet) > 0 && (
              <div className="text-right bg-white/5 border border-white/10 px-2.5 py-1 rounded-xl shrink-0">
                <span className="text-[11px] text-amber-300 font-semibold block font-mono">
                  {getReservedBalance(wallet).toLocaleString()} Ks
                </span>
                <span className="text-[9px] text-white/50 block">{t.reserved}</span>
              </div>
            )}
          </div>

          {/* Bottom Row: 3 Action Buttons */}
          <div className="grid grid-cols-3 gap-2 mt-3 pt-2.5 border-t border-white/10">
            <button
              onClick={handleOpenDeposit}
              className="flex items-center justify-center space-x-1 bg-primary hover:bg-primary-hover active:scale-[0.98] font-bold py-2 px-2 rounded-xl text-xs transition shadow-sm text-white touch-target"
            >
              <PlusCircle className="w-3.5 h-3.5 text-white" />
              <span className="text-white font-bold">{t.deposit}</span>
            </button>

            <button
              onClick={handleOpenBuyBill}
              className="flex items-center justify-center space-x-1 bg-sky-500/20 hover:bg-sky-500/30 active:scale-[0.98] text-white border border-sky-400/30 font-semibold py-2 px-2 rounded-xl text-xs transition backdrop-blur-xs touch-target"
            >
              <PhoneCall className="w-3.5 h-3.5 text-sky-400" />
              <span className="font-semibold">{t.buyBill}</span>
            </button>

            <button
              onClick={handleOpenSellBill}
              className="flex items-center justify-center space-x-1 bg-white/10 hover:bg-white/15 active:scale-[0.98] text-white border border-white/20 font-semibold py-2 px-2 rounded-xl text-xs transition backdrop-blur-xs touch-target"
            >
              <ArrowLeftRight className="w-3.5 h-3.5 text-white" />
              <span>{t.sellBill}</span>
            </button>
          </div>
        </div>
      )}

      {/* Operator Availability Selector (Single Row) */}
      <div>
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-bold text-plum-dark">Operator ရွေးချယ်ပါ</h2>
          <span className="text-[11px] text-plum-muted">Daily Limit စစ်ဆေးပြီး</span>
        </div>

        <OperatorSelector
          operators={operators}
          selectedId={selectedOperatorId}
          onSelect={setSelectedOperatorId}
        />
      </div>

      {/* Closed Operator Alert Notice */}
      {currentOperator && currentOperator.package_status !== 'open' && (
        <div className="p-3.5 bg-amber-50/90 border border-amber-200 rounded-2xl text-xs text-amber-900 flex items-start space-x-2.5 shadow-xs animate-in fade-in">
          <ShieldAlert className="w-4 h-4 text-status-amber shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">
              {currentOperator.name} Package အရောင်းကို ယာယီပိတ်ထားပါသည်
            </p>
            <p className="text-[11px] text-amber-800/80 mt-0.5">
              {currentOperator.package_status === 'closed_daily_limit'
                ? 'ယနေ့အတွက် data package လွှဲပေးနိုင်သည့် daily transfer limit ပြည့်သွားသဖြင့် ခေတ္တပိတ်ထားပါသည်။ အခြား operator ကို ရွေးချယ်နိုင်ပါသည်။'
                : currentOperator.status_reason || 'ယာယီပြင်ဆင်နေပါသည်'}
            </p>
          </div>
        </div>
      )}

      {/* Package List */}
      <div>
        <h3 className="text-xs font-bold text-plum-muted uppercase tracking-wider mb-2.5 px-1">
          {currentOperator?.name} Data Packages ({packages.length})
        </h3>

        {packages.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            operator={currentOperator}
            onBuy={(selected) => handleSelectPackage(selected)}
          />
        ))}

        {packages.length === 0 && !loading && (
          <div className="p-8 text-center bg-surface-card rounded-2xl border border-plum-border text-plum-muted text-xs">
            လတ်တလော package များ မရှိသေးပါ။
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      {selectedPackage && currentOperator && (
        <CheckoutModal
          pkg={selectedPackage}
          operator={currentOperator}
          wallet={wallet || undefined}
          onClose={() => setSelectedPackage(null)}
          onOrderSuccess={(orderId) => {
            setSelectedPackage(null);
            loadData();
            onNavigateToOrders();
          }}
        />
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          onClose={() => setShowDepositModal(false)}
          onSuccess={() => {
            loadData();
          }}
        />
      )}

      {/* Sell Bill Modal */}
      {showSellModal && (
        <SellBillModal
          onClose={() => setShowSellModal(false)}
          onSuccess={() => {
            loadData();
            onNavigateToOrders();
          }}
        />
      )}

      {/* Buy Bill Modal */}
      {showBuyModal && (
        <BuyBillModal
          operators={operators}
          wallet={wallet}
          initialOperatorId={selectedOperatorId}
          onClose={() => setShowBuyModal(false)}
          onSuccess={() => {
            loadData();
            onNavigateToOrders();
          }}
        />
      )}
    </div>
  );
};
